/**
 * Formats a number as a currency string
 * @param value - The value to format
 * @param currency - The currency code (default: USD)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number, currency = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

/**
 * Formats a number as a percentage string
 * @param value - The percentage value (0.5 = 50%)
 * @param fractionDigits - Number of fraction digits to display
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, fractionDigits = 2): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(value / 100);
};

/**
 * Formats a dollar amount compactly for dense tables, e.g. $540, $3.1K, -$118.2M, $1.2B
 * @param value - The dollar amount
 * @returns Compact dollar string with a leading minus sign for losses
 */
export const formatCompactDollars = (value: number): string => {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
};
